<template>
  <div class="modal-overlay" v-if="isVisible" @click.self="close">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('reportAdModal.title') }}</h5>
          <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <label for="report-list">{{ $t('reportAdModal.default_reasons') }}</label>

          <div class="col-12">
            <select class="form-control" id="report-list" v-model="selectedReason">
              <option value="" disabled>{{ $t('reportAdModal.select_reason') }}</option>
              <option v-for="r in reasons" :key="r.value" :value="r.value">{{ r.label }}</option>
            </select>
          </div>

          <section class="custom-reason" v-if="selectedReason === 'other'">
            <textarea v-model="customReason" :placeholder="$t('reportAdModal.custom_placeholder')" rows="3"></textarea>
          </section>

          <div class="report-result" v-if="reportResult">{{ reportResult }}</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-primary" @click="close">{{ $t('reportAdModal.cancel') }}</button>
          <button type="button" class="btn btn-danger" @click="sendReport">{{ $t('reportAdModal.submit') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()
const props = defineProps({
  product: { type: Object, required: true }
})
const emit = defineEmits(['close'])

const isVisible = ref(true)
const selectedReason = ref('')
const customReason = ref('')
const reportResult = ref('')

const reasons = computed(() => [
  { value: t('reportAdModal.reason_1'), label: t('reportAdModal.reason_1') },
  { value: t('reportAdModal.reason_2'), label: t('reportAdModal.reason_2') },
  { value: t('reportAdModal.reason_3'), label: t('reportAdModal.reason_3') },
  { value: t('reportAdModal.reason_4'), label: t('reportAdModal.reason_4') },
  { value: t('reportAdModal.reason_5'), label: t('reportAdModal.reason_5') },
  { value: t('reportAdModal.reason_6'), label: t('reportAdModal.reason_6') },
  { value: 'other', label: t('reportAdModal.reason_other') }
])

const close = () => {
  isVisible.value = false
  emit('close')
}

watch(selectedReason, (val) => {
  if (val !== 'other') {
    customReason.value = ''
  }
})

const sendReport = async () => {
  const reason = selectedReason.value === 'other' ? customReason.value : selectedReason.value
  if (!reason) {
    reportResult.value = t('reportAdModal.select_reason_error')
    return
  }

  try {
    await $fetch('/market/api/products/report', {
      method: 'POST',
      body: {
        ad_id: props.product.id,
        reason: reason
      }
    })
    reportResult.value = t('reportAdModal.success')
    setTimeout(() => close(), 1500)
  } catch (e) {
    reportResult.value = t('reportAdModal.error')
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-dialog {
  width: 100%;
  max-width: 500px;
  margin: auto;
}

.modal-content {
  background: #fff;
  border-radius: $r-lg;
  overflow: hidden;
  box-shadow: $shadow-warm-lg;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid $line;

  .modal-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: $ink;
  }

  .btn-close {
    background: transparent;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
  }
}

.modal-body {
  padding: 20px;
  font-size: 0.88rem;
  line-height: 1.8;
  color: $ink;

  > label {
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
    font-size: 0.85rem;
  }

  .form-control {
    border-radius: $r-sm;
    border: 1px solid #ddd;
    padding: 10px 15px;
    font-family: inherit;
    width: 100%;
    background: #fff;

    &:focus {
      border-color: $leaf;
      box-shadow: 0 0 0 0.2rem rgba(3, 160, 84, 0.25);
      outline: none;
    }
  }

  .custom-reason {
    margin-top: 12px;

    textarea {
      width: 100%;
      padding: 10px 15px;
      border: 1px solid #ddd;
      border-radius: $r-sm;
      font-family: inherit;
      resize: vertical;

      &:focus {
        border-color: $leaf;
        box-shadow: 0 0 0 0.2rem rgba(3, 160, 84, 0.25);
        outline: none;
      }
    }
  }

  .report-result {
    margin-top: 12px;
    padding: 8px 12px;
    border-radius: $r-sm;
    font-size: 0.85rem;
    text-align: center;
    color: $leaf;
    background: rgba(3, 160, 84, 0.06);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid $line;

  .btn {
    padding: 8px 18px;
    border-radius: $r-pill;
    font-size: 0.84rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.25s;
    border: 1.5px solid;

    &.btn-outline-primary {
      background: transparent;
      border-color: $leaf;
      color: $leaf;

      &:hover { background: $leaf; color: #fff; }
      &:active { transform: scale(0.97); }
    }

    &.btn-danger {
      background: $berry;
      border-color: $berry;
      color: #fff;

      &:hover { background: $berry-deep; border-color: $berry-deep; }
      &:active { transform: scale(0.97); }
    }
  }
}
</style>
